import { combineReducers } from "redux";
import * as actions from "../actions";

// ===== 배송주문 관리 — CS(클레임/문의/수집·승인/결제·정산) =====
// list 계열 응답은 서버 직렬화 미확정(CSTask/CSActivity) — 필드 접근은 라벨 헬퍼를
// 거쳐 방어적으로. 배송단계 라벨은 helpers/csActionMatrix.getDeliveryStage 재사용.

// CS 유형 라벨 — CSTask.type
// 결제대기/지급대기/채널미반영 — CSType 신규 3종(설계 §상태 3종 표면).
// 서버 CSType ALTER 미배포면 type 이 응답에 안 와 표면(뱃지/버튼)이 자연히 숨음 — 방어 불요.
export const CS_TYPE_LABELS = {
  DELIVERY_CANCEL: "취소",
  EXCHANGE: "교환",
  RETURN_REFUND: "반품",
  GENERAL: "문의",
  DELIVERY_HOLD: "보류",
  PAYMENT_PENDING: "결제대기",
  REFUND_PENDING: "지급대기",
  CHANNEL_UNSYNCED: "채널미반영",
};

// searchCsOrders matchedBy → 배지 라벨 — 서버 확정 5종(2026-07-21) + 수령자명(서버 7/23):
// originId(주문번호)·number(관리번호)·trackingNumber(송장번호)·claimCode(클레임코드)·
// pickupTrackingNumber(반송장)·receiverName(수령자명 부분일치).
// (구 orderNumber/returnTrackingNumber 는 미사용 — 제거.)
export const CS_MATCHED_BY_LABELS = {
  originId: "주문번호",
  number: "관리번호",
  trackingNumber: "송장번호",
  claimCode: "클레임코드",
  pickupTrackingNumber: "반송장",
  receiverName: "수령자명",
};

// CS 상태 라벨 — 기획 §3.2 색 기준: 🔴 접수(처리 필요) · 🟠 진행중 · 🟢 완료
export const CS_STATUS_LABELS = {
  TODO: "접수",
  IN_PROGRESS: "진행중",
  DONE: "완료",
};

// 클레임 사유 코드 → 한글 라벨 (front 표시 참조 — 수집함·리스트·이력 공용).
// 서버 글로벌 참조(claim-reason-global-reference-prompt.md)의 시맨틱 canonical 코드와
// 동기화. 현행 데이터는 cafe24 letter(A~I)라 letter 도 함께 매핑(전환 과도기 — 서버가
// 시맨틱으로 전환되면 letter 분기는 자연 미사용). getClaimReasonLabel 로 둘 다 흡수.
export const CLAIM_REASON_LABELS = {
  change_of_mind: "단순 변심",
  delivery_delay: "배송 지연",
  product_defect: "상품 불량",
  wrong_delivery: "오배송",
  delivery_damage: "배송 파손",
  info_mismatch: "상품 정보 상이",
  out_of_stock: "상품 품절",
  etc: "기타",
  // 수집 매치리즌 — 클레임 수집이 만든 task 는 reasonCode 에 사유 대신 수집 종류가 실림
  cancel_request: "고객 취소 요청 (승인 대기)",
};

// cafe24 claim_reason_type letter → 시맨틱(레거시 데이터 표시용)
const CAFE24_REASON_TO_SEMANTIC = {
  A: "change_of_mind",
  B: "delivery_delay",
  D: "delivery_damage",
  E: "product_defect",
  F: "info_mismatch",
  H: "out_of_stock",
  I: "etc",
};

// 사유 코드 → 한글 라벨. 시맨틱·cafe24 letter 둘 다 흡수, 미매핑은 원문(방어).
export const getClaimReasonLabel = (code) => {
  if (!code) return null;
  const key = String(code);
  if (CLAIM_REASON_LABELS[key]) return CLAIM_REASON_LABELS[key];
  const semantic = CAFE24_REASON_TO_SEMANTIC[key];
  if (semantic && CLAIM_REASON_LABELS[semantic]) return CLAIM_REASON_LABELS[semantic];
  return key;
};

// 상태 → ds-cs-pill/tag 변형 (red=접수/amber=진행중/green=완료)
const CS_STATUS_VARIANTS = {
  TODO: "red",
  IN_PROGRESS: "amber",
  DONE: "green",
};

// task 배지 산출 — {label, typeLabel, statusLabel, variant}. 미확정 필드 방어.
// label 규칙(우진 2026-07-21): 활성(접수/진행중)만 "{유형} {상태}", 완료·철회(DONE/
// CANCELED)는 처리가 끝나 정상으로 복귀한 것 → "정상"(green). 예: 채널미반영 반영완료
// = 정상이지 "채널미반영 완료"가 아님.
export const getCsBadge = (task) => {
  const type = (task?.type || "").toUpperCase();
  const status = (task?.status || "").toUpperCase();
  const resolved = status === "DONE" || status === "CANCELED";
  const typeLabel = CS_TYPE_LABELS[type] || task?.type || "-";
  const statusLabel = CS_STATUS_LABELS[status] || task?.status || "-";
  return {
    label: resolved ? "정상" : `${typeLabel} ${statusLabel}`,
    typeLabel,
    statusLabel,
    variant: resolved ? "green" : CS_STATUS_VARIANTS[status] || "amber",
  };
};

const initialStatus = {
  isFetching: false,
  error: null,
};

// 서브도메인별 status 리듀서 생성기 — fetching 트리거 액션 / SUCCESS / FAILURE 매핑
// (reducers/delivery.js 55행과 동일 시그니처 — 파일 로컬 관례)
function makeStatus(fetchingTypes, successType, failureType) {
  return function status(state = initialStatus, action) {
    if (fetchingTypes.includes(action.type)) {
      return { isFetching: true, error: null };
    }
    switch (action.type) {
      case successType:
        return initialStatus;
      case failureType:
        return { isFetching: false, error: action.error };
      default:
        return state;
    }
  };
}

const status = makeStatus(
  [
    actions.GET_CS_TASKS,
    // 주문 단위 목록(USE_CS_ORDERS) 재조회도 fetching 트리거 — 리스트 로딩 표시 공백 보정(우진 7/23)
    actions.GET_CS_ORDERS,
    actions.GET_CS_COUNTS,
    actions.GET_CS_ACTIVITIES,
    actions.SEARCH_CS_ORDERS,
  ],
  actions.SET_CS_STATUS_SUCCESS,
  actions.SET_CS_STATUS_FAILURE,
);

const claimStatus = makeStatus(
  [
    actions.CS_CANCEL_ORDER,
    actions.CS_RESTORE_ORDER,
    actions.CS_RESTORE_RETURN,
    actions.CS_RESTORE_EXCHANGE,
    actions.CS_ACCEPT_RETURN,
    actions.CS_RECEIVE_RETURN,
    actions.CS_COMPLETE_RETURN,
    actions.CS_ACCEPT_EXCHANGE,
    actions.CS_RECEIVE_EXCHANGE,
    actions.CS_COMPLETE_EXCHANGE,
    actions.CS_COMPLETE_CANCEL,
    actions.CS_ISSUE_PICKUP_TRACKING,
  ],
  actions.SET_CS_CLAIM_STATUS_SUCCESS,
  actions.SET_CS_CLAIM_STATUS_FAILURE,
);

const editStatus = makeStatus(
  [
    actions.CS_SET_HOLD,
    actions.CS_BULK_SET_HOLD,
    actions.CS_UPDATE_RECEIVER,
    actions.CS_ADD_ORDER_ITEMS,
    actions.CS_SWAP_ORDER_ITEM,
    actions.CS_HANDLE_SHORTAGE,
    actions.CS_OPEN_INQUIRY,
    actions.CS_COMPLETE_INQUIRY,
    actions.CS_RESYNC_CHANNEL,
  ],
  actions.SET_CS_EDIT_STATUS_SUCCESS,
  actions.SET_CS_EDIT_STATUS_FAILURE,
);

const collectStatus = makeStatus(
  [
    actions.CS_COLLECT_CLAIMS,
    actions.CS_COLLECT_INQUIRIES,
    actions.CS_APPROVE_CLAIM,
    actions.CS_REJECT_CLAIM,
  ],
  actions.SET_CS_COLLECT_STATUS_SUCCESS,
  actions.SET_CS_COLLECT_STATUS_FAILURE,
);

const paymentStatus = makeStatus(
  [actions.CS_CONFIRM_PAYMENT, actions.CS_SETTLE_RETURN, actions.CS_REFUND_TRANSFER],
  actions.SET_CS_PAYMENT_STATUS_SUCCESS,
  actions.SET_CS_PAYMENT_STATUS_FAILURE,
);

// CS 작업 목록 — listCsTasks 응답 그대로 {total, page, size, rows}
// row: id,orderId,type,status,reasonCode,providerClaimId,orderItemIds,attachments,
//      assigneeId,dueAt,createdAt,updatedAt,originId,origin,number,numberPrefix,
//      orderName,retailState (manual 주문은 표시 컬럼 None 방어)
const INITIAL_TASKS = { total: 0, page: 1, size: 50, rows: [] };

function tasks(state = INITIAL_TASKS, action) {
  switch (action.type) {
    case actions.RECEIVE_CS_TASKS:
      return action.tasks || INITIAL_TASKS;
    default:
      return state;
  }
}

// KPI 카운트 — countCsTasks 응답 {byStatus, byType, activeByType}
const INITIAL_COUNTS = { byStatus: {}, byType: {}, activeByType: {} };

function counts(state = INITIAL_COUNTS, action) {
  switch (action.type) {
    case actions.RECEIVE_CS_COUNTS:
      return action.counts || INITIAL_COUNTS;
    default:
      return state;
  }
}

// 주문별 CS 이력 — { [orderId]: [{id,orderId,taskId,actorId,actionType,payload,createdAt}] }
function activitiesByOrder(state = {}, action) {
  switch (action.type) {
    case actions.RECEIVE_CS_ACTIVITIES: {
      if (!action.orderId) return state;
      return { ...state, [action.orderId]: action.activities || [] };
    }
    default:
      return state;
  }
}

// 주문 검색 결과 — searchCsOrders rows(+matchedBy) | null (검색바 소비 후 clear)
function searchResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_CS_SEARCH_RESULT:
      return action.result;
    case actions.CLEAR_CS_SEARCH_RESULT:
    case actions.SEARCH_CS_ORDERS:
      return null;
    default:
      return state;
  }
}

// 공용 쓰기 결과 — { actionKey, result }. 게이팅 에러({error, needsRefundBank|
// needsDeleteTracking|feePending|duplicatedItemIds}) 포함 — 패널이 소비 후 CLEAR.
function actionResult(state = null, action) {
  switch (action.type) {
    case actions.RECEIVE_CS_ACTION_RESULT:
      return { actionKey: action.actionKey, result: action.result };
    case actions.CLEAR_CS_ACTION_RESULT:
      return null;
    default:
      return state;
  }
}

// 발송/배송완료 push 직후 낙관 반영 — { orderId: retailState }.
// 발주확인·발번과 같은 문제: Order GSI 가 eventual consistent 라 push 직후 재조회가
// 옛 retailState 를 돌려줘 배송 상태 배지가 안 바뀐다(2026-09-08 실측). 서버가 sent 로
// 확정한 주문만 기억하고, 재조회 행이 같은(또는 더 진행된) 상태로 오면 그때 잊는다.
const RETAIL_RANK = { unconfirmed: 0, confirmed: 1, shipped: 2, delivered: 3 };

function stageOverrides(state = {}, action) {
  switch (action.type) {
    case actions.RECEIVE_PUSH_SHIPMENT_RESULT: {
      const results = (action.result && action.result.results) || {};
      const target = action.status === "shipped" ? "delivered" : "shipped";
      const next = { ...state };
      Object.keys(results).forEach((oid) => {
        const r = results[oid] || {};
        if (Array.isArray(r.sent) && r.sent.length > 0) next[oid] = target;
      });
      return next;
    }
    case actions.RECEIVE_CS_TASKS:
    case actions.RECEIVE_CS_SEARCH_RESULT: {
      const rows = action.type === actions.RECEIVE_CS_TASKS
        ? (action.tasks && action.tasks.rows) || []
        : (action.result && action.result.rows) || action.result || [];
      const next = { ...state };
      (Array.isArray(rows) ? rows : []).forEach((row) => {
        const oid = row.orderId || row.id;
        const want = next[oid];
        if (!want) return;
        const have = (row.retailState || "").toLowerCase();
        if ((RETAIL_RANK[have] ?? -1) >= RETAIL_RANK[want]) delete next[oid];
      });
      return next;
    }
    default:
      return state;
  }
}

const applyStageOverrides = (rows, overrides) => {
  if (!Array.isArray(rows) || !Object.keys(overrides).length) return rows;
  let changed = false;
  const out = rows.map((row) => {
    const want = overrides[row.orderId || row.id];
    if (!want || (row.retailState || "").toLowerCase() === want) return row;
    changed = true;
    return { ...row, retailState: want };
  });
  return changed ? out : rows;
};

const combined = combineReducers({
  status,
  claimStatus,
  editStatus,
  collectStatus,
  paymentStatus,
  tasks,
  counts,
  activitiesByOrder,
  searchResult,
  actionResult,
  stageOverrides,
});

export default function cs(state, action) {
  const next = combined(state, action);
  if (
    action.type !== actions.RECEIVE_PUSH_SHIPMENT_RESULT &&
    action.type !== actions.RECEIVE_CS_TASKS &&
    action.type !== actions.RECEIVE_CS_SEARCH_RESULT
  ) {
    return next;
  }
  const overrides = next.stageOverrides || {};
  const rows = applyStageOverrides(next.tasks.rows, overrides);
  const search = next.searchResult;
  const searchRows = search
    ? applyStageOverrides(Array.isArray(search) ? search : search.rows, overrides)
    : null;
  const tasksNext = rows === next.tasks.rows ? next.tasks : { ...next.tasks, rows };
  let searchNext = search;
  if (search && searchRows !== (Array.isArray(search) ? search : search.rows)) {
    searchNext = Array.isArray(search) ? searchRows : { ...search, rows: searchRows };
  }
  if (tasksNext === next.tasks && searchNext === search) return next;
  return { ...next, tasks: tasksNext, searchResult: searchNext };
}
